import React, { useContext, useMemo } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import { LineItem } from '@r6digital/rapidstor-types/types/Merchandise';
import { CostRetrieveResponse } from '@r6digital/rapidstor-types/types/Reservation';

import { descriptorDimensions } from '@/core/utils/rapidstor';
import { formatCurrency } from '@/core/utils/currency';
import { descriptorTitle, transformCostRetrieve } from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import classes from './styles.module.scss';

interface Props {
    descriptor: Descriptor | undefined;
    unitType: UnitType | undefined;
    deal: Deal | undefined;
    costRetrieve: CostRetrieveResponse | undefined;
    cart: LineItem[];
    merchandiseDiscount: number;
}

export default function SimplifiedCheckoutSummary({
    descriptor,
    unitType,
    deal,
    costRetrieve,
    cart,
    merchandiseDiscount,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { rapidstorMainColor } = useContext(JourneyContext);
    const { currencySymbol } = location.rapidstor;
    const skeletonColor = rapidstorMainColor ?? location.skin.mainColour;

    const lineItems = useMemo(() => {
        if (!costRetrieve) return [];
        return transformCostRetrieve(
            undefined,
            costRetrieve,
            cart,
            merchandiseDiscount,
        ).filter((item) => item.chargeAmount > 0 || item.discountAmount > 0);
    }, [costRetrieve, cart, merchandiseDiscount]);

    const getItemTotal = (item: ReturnType<typeof transformCostRetrieve>[number]) => item.total ?? item.chargeAmount + item.taxAmount - item.discountAmount;

    const totals = useMemo(() => {
        const total = lineItems.reduce((acc, item) => acc + getItemTotal(item), 0);
        const taxes = lineItems.reduce((acc, item) => acc + item.taxAmount, 0);
        const subtotal = total - taxes;
        return { subtotal, taxes, total };
    }, [lineItems]);

    const locationAddress = useMemo(() => {
        const parts = [
            location.address.address1,
            location.address.city,
            location.address.region,
            location.address.postalCode,
        ].filter(Boolean);
        return parts.join(', ');
    }, [location.address]);

    const unitName = useMemo(() => {
        if (!unitType) return location.name;
        return `${location.name} - ${descriptorDimensions(unitType, location, { supplementary: false })}`;
    }, [location, unitType]);

    const pricing = useMemo(() => {
        const displayRate = deal?.rates.discountedRate
            ?? unitType?.rates.discountedRate
            ?? unitType?.rates.rate
            ?? 0;
        const standardRate = deal?.rates.rate ?? unitType?.rates.rate ?? 0;
        const saved = deal?.rates.discountSaved ?? 0;
        return {
 displayRate, standardRate, hasSavings: saved > 0, saved,
};
    }, [deal, unitType]);

    const isLoadingPricing = !costRetrieve;

    return (
        <div className={classes.summaryColumn}>
            <div className={classes.summaryCard}>
                {/* Unit information */}
                <div className={classes.unitInfoSection}>
                    <div className={classes.unitHeader}>
                        <div className={classes.summaryTitle}>{t('wizard:unitInformation')}</div>
                        <div className={classes.unitNameGroup}>
                            <span className={classes.unitName}>{unitName}</span>
                            {locationAddress && (
                                <div className={classes.unitAddress}>{locationAddress}</div>
                            )}
                        </div>
                    </div>

                    {descriptor && unitType ? (
                        <div className={classes.unitImageRow}>
                            <div>
                                <img
                                    className={classes.unitImage}
                                    src={descriptor.picture || location.skin.defaultUnitPicture}
                                    alt={descriptor.name}
                                />
                                <span className={classes.unitTitle}>{descriptorTitle(descriptor, location)[1]}</span>
                            </div>
                            <div className={classes.priceBlock}>
                                <span className={classes.priceCurrent}>
                                    {currencySymbol}
                                    {formatCurrency(pricing.displayRate, location)}
                                </span>
                                {pricing.hasSavings && (
                                    <div className={classes.priceSavings}>
                                        <span className={classes.priceSave}>
                                            {t('wizard:pricingTable:save')}
                                            {' '}
                                            {currencySymbol}
                                            {formatCurrency(pricing.saved, location)}
                                        </span>
                                        <span className={classes.priceWas}>
                                            (
                                            {t('wizard:pricingTable:was')}
                                            {' '}
                                            {currencySymbol}
                                            {formatCurrency(pricing.standardRate, location)}
                                            )
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <ContentLoader
                            foregroundColor={skeletonColor}
                            backgroundColor="#fff"
                            width="100%"
                            height="96"
                        >
                            <rect x="0" y="0" width="181" height="96" rx="4" />
                        </ContentLoader>
                    )}
                </div>

                {/* Receipt */}
                <div className={classes.pricingSection}>
                    <div className={classes.receiptHeading}>{t('wizard:pricingTable:receipt')}</div>

                    <div className={classes.pricingDivider} />

                    {isLoadingPricing ? (
                        <div className={classes.pricingSkeletonContainer}>
                            {[0, 1, 2].map((i) => (
                                <div key={i} className={classes.pricingRow}>
                                    <ContentLoader
                                        foregroundColor={skeletonColor}
                                        backgroundColor="#f0f0f0"
                                        width="100%"
                                        height="20"
                                        style={{ width: '100%' }}
                                    >
                                        <rect x="0" y="3" width="40%" height="14" rx="3" />
                                        <rect x="80%" y="3" width="20%" height="14" rx="3" />
                                    </ContentLoader>
                                </div>
                            ))}

                            <div className={classes.pricingDivider} />

                            {[0, 1].map((i) => (
                                <div key={`summary_${i}`} className={classes.pricingRow}>
                                    <ContentLoader
                                        foregroundColor={skeletonColor}
                                        backgroundColor="#f0f0f0"
                                        width="100%"
                                        height="20"
                                        style={{ width: '100%' }}
                                    >
                                        <rect x="30%" y="3" width="25%" height="14" rx="3" />
                                        <rect x="80%" y="3" width="20%" height="14" rx="3" />
                                    </ContentLoader>
                                </div>
                            ))}

                            <div className={classes.pricingDivider} />

                            <div className={classes.pricingRow}>
                                <ContentLoader
                                    foregroundColor={skeletonColor}
                                    backgroundColor="#f0f0f0"
                                    width="100%"
                                    height="20"
                                    style={{ width: '100%' }}
                                >
                                    <rect x="30%" y="3" width="25%" height="14" rx="3" />
                                    <rect x="75%" y="3" width="25%" height="14" rx="3" />
                                </ContentLoader>
                            </div>
                        </div>
                    ) : (
                        <>
                            {lineItems.map((item, index) => (
                                <div key={`${index}_line`} className={classes.pricingRow}>
                                    <span>
                                        {item.chargeDescription}
                                        {item.chargePeriod && (
                                            <span className={classes.chargePeriod}>
                                                {item.chargePeriod}
                                            </span>
                                        )}
                                    </span>
                                    <span>
                                        {currencySymbol}
                                        {getItemTotal(item).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                            <div className={classes.pricingDivider} />
                            <div className={classes.pricingRow}>
                                <span>{t('wizard:pricingTable:subtotal')}</span>
                                <span>
                                    {currencySymbol}
                                    {totals.subtotal.toFixed(2)}
                                </span>
                            </div>
                            <div className={classes.pricingRow}>
                                <span>{t('wizard:pricingTable:tax')}</span>
                                <span>
                                    {currencySymbol}
                                    {totals.taxes.toFixed(2)}
                                </span>
                            </div>
                            <div className={classes.pricingDivider} />
                            <div className={`${classes.pricingRow} ${classes.pricingTotal}`}>
                                <span>{t('wizard:pricingTable:totalDueToday')}</span>
                                <span>
                                    {currencySymbol}
                                    {totals.total.toFixed(2)}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
